import { useState } from "react";
import { motion } from "framer-motion";
import { HeartHandshake } from "lucide-react";
import { EASE, fmtUSD } from "../lib/site";
import { PledgeModal } from "./PledgeModal";

const PRESETS = [
  { amount: 25, impact: "10 warm meals for a family in Newark this week" },
  { amount: 50, impact: "a full week of groceries for a household in crisis" },
  { amount: 100, impact: "an urgent-care visit and medicine for one patient" },
  { amount: 250, impact: "a term of school supplies for ten children" },
];

export const Donate = () => {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [open, setOpen] = useState(false);

  const effective = custom ? Number(custom) || 0 : amount;
  const impact = custom
    ? "every dollar you give goes directly to a family in need"
    : PRESETS.find((p) => p.amount === amount)?.impact;

  return (
    <section id="donate" data-testid="donate-section" className="py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <p className="eyebrow">The Million Hope Initiative</p>
          <h2 className="mt-5 font-serif text-4xl font-medium leading-[1.15] tracking-tight text-ink sm:text-5xl">
            Your gift, <em className="text-brand">working by Friday.</em>
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-inksoft">
            We don't warehouse generosity. Gifts made this week are converted
            into meals, rent relief, medicine and school supplies within days —
            and every gift is acknowledged personally.
          </p>

          <div className="mt-10 rounded-[28px] border border-line bg-linen/70 p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-inksoft">
              Your impact preview
            </p>
            <p className="mt-4 font-serif text-2xl leading-snug text-ink sm:text-3xl" data-testid="donate-impact-preview">
              <span className="font-semibold text-brand">{fmtUSD(effective)}</span>{" "}
              provides {impact}.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="rounded-[32px] border border-line bg-cardx p-8 shadow-xl sm:p-10"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand/10 text-brand">
              <HeartHandshake className="h-5 w-5" />
            </span>
            <div>
              <h3 className="font-serif text-2xl font-semibold text-ink">Make a donation</h3>
              <p className="text-xs text-inksoft">Choose an amount to see what it does</p>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3">
            {PRESETS.map((p) => {
              const active = !custom && amount === p.amount;
              return (
                <button
                  key={p.amount}
                  data-testid={`btn-donation-amount-${p.amount}`}
                  onClick={() => {
                    setAmount(p.amount);
                    setCustom("");
                  }}
                  className={`rounded-2xl border px-4 py-5 font-serif text-2xl font-semibold transition-all duration-300 ${
                    active
                      ? "border-brand bg-brand text-[#FDFBF7] shadow-lg shadow-brand/20"
                      : "border-line bg-paper text-ink hover:border-brand hover:text-brand"
                  }`}
                >
                  ${p.amount}
                </button>
              );
            })}
          </div>

          <div className="relative mt-3">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-serif text-xl text-inksoft">
              $
            </span>
            <input
              data-testid="input-custom-donation-amount"
              type="number"
              min="1"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Custom amount"
              className="input-field py-4 pl-9 font-serif text-xl"
            />
          </div>

          <button
            data-testid="btn-open-pledge-modal"
            onClick={() => effective > 0 && setOpen(true)}
            disabled={effective <= 0}
            className="mt-6 w-full rounded-full bg-brand py-4 text-sm font-semibold uppercase tracking-[0.15em] text-[#FDFBF7] transition-colors hover:bg-branddeep disabled:cursor-not-allowed disabled:opacity-40"
          >
            Donate {effective > 0 ? fmtUSD(effective) : ""}
          </button>
          <p className="mt-4 text-center text-[11px] leading-relaxed text-inksoft">
            Secure card payment processed by Stripe. DAIO is a registered
            501(c)(3) nonprofit (EIN 45-3932747); gifts are tax-deductible to the
            extent allowed by law.
          </p>
        </motion.div>
      </div>

      <PledgeModal open={open} amount={effective} onClose={() => setOpen(false)} />
    </section>
  );
};
